import { Inject, Injectable } from '@angular/core';
import {
  AppParametersService,
  BaseAppParameters,
  Category,
  CategoryFilterOption,
  ContractType,
  ContractTypeOption,
  ENV,
  FilterOption,
  LivingSpaceOption,
  LivingSpaceRange,
  PriceOption,
  PriceRange,
  PropertySpaceOption,
  PropertySpaceRange,
  Range,
  RoomCountOption,
  RoomCountRange,
  Subcategory,
  SubcategoryOptionsMap,
  UsageSpaceOption,
  UsageSpaceRange,
} from '@immosky/types';
import {
  availableContractTypeOptions,
  categoryFilterOptions,
  categoryToSubcategoriesMap,
  cloneSubcategoryFilter,
  getLivingSpaceOptionsForContractType,
  getPriceOptionsForContractType,
  getPropertySpaceOptionsForContractType,
  getRoomCountOptionsForContractType,
  getUsageSpaceForContractType,
} from '@immosky/object-search-filter';
import { ObjectSearchEnvironmentConfig } from '../../environments/object-search-environment-config';

export interface ObjectSearchAppParametersInput extends BaseAppParameters {
  googleMapsApiKey: string;
  mapGridSize: number;
  detailPageUrl: string;
  initialFilters: {
    contractType: ContractType;
    category: {
      main: Category;
      sub: Subcategory[];
    };
    price: Range<number | null>;
    roomCount: Range<number | null>;
    livingSpace: Range<number | null>;
    usageSpace: Range<number | null>;
    propertySpace: Range<number | null>;
  };
}

export interface ObjectSearchAppParameters extends BaseAppParameters {
  googleMapsApiKey: string;
  mapGridSize: number;
  detailPageUrl: string;
  searchSubscriptionNewsletterSignupUrl?: string;
  initialFilters?: Partial<{
    contractType: ContractTypeOption;
    category: Partial<{
      main: CategoryFilterOption;
      sub: SubcategoryOptionsMap;
    }>;
    price: PriceRange;
    roomCount: RoomCountRange;
    livingSpace: LivingSpaceRange;
    usageSpace: UsageSpaceRange;
    propertySpace: PropertySpaceRange;
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class ObjectSearchAppParametersService extends AppParametersService<ObjectSearchAppParameters> {
  protected parameters: ObjectSearchAppParameters = {
    apiBaseUrl: this.environment.apiBaseUrl,
    apiBasicAuth: this.environment.apiBasicAuth,
    googleMapsApiKey: this.environment.googleMapsApiKey,
    mapGridSize: 0,
    language: this.environment.language,
    detailPageUrl: this.environment.detailPageUrl,
    countries: this.environment.countries,
    propertiesIndex: 'properties-staging',
    regionsIndex: 'regions-staging',
    branding: 'immosky',
    isResidia: this.environment.isResidia,
  };

  constructor(
    @Inject(ENV) protected environment: ObjectSearchEnvironmentConfig
  ) {
    super(environment);
  }

  protected setAppParameters(
    inputConfig: ObjectSearchAppParametersInput
  ): void {
    this.updateMapsConfig(inputConfig);
    this.updateDetailPageUrl(inputConfig);
    this.updateInitialFilters(inputConfig);
    this.updateSearchBackendConfig(inputConfig);
    this.updateCountries(inputConfig);
    this.updateBranding(inputConfig);
    this.updateIsResidia(inputConfig);
  }

  private updateIsResidia(inputConfig: ObjectSearchAppParametersInput) {
    this.parameters.isResidia = inputConfig.isResidia ? inputConfig.isResidia : false;
  }

  private updateCountries(inputConfig: ObjectSearchAppParametersInput) {
    if (inputConfig.countries) {
      this.parameters.countries = inputConfig.countries;
    }
  }

  private updateBranding(inputConfig: ObjectSearchAppParametersInput) {
    if (inputConfig.branding) {
      this.parameters.branding = inputConfig.branding;
    }
  }

  private updateSearchBackendConfig(
    inputConfig: ObjectSearchAppParametersInput
  ) {
    if (inputConfig.apiBaseUrl)
      this.parameters.apiBaseUrl = inputConfig.apiBaseUrl;
    if (inputConfig.apiBasicAuth)
      this.parameters.apiBasicAuth = inputConfig.apiBasicAuth;
    if (inputConfig.regionsIndex)
      this.parameters.regionsIndex = inputConfig.regionsIndex;
    if (inputConfig.propertiesIndex)
      this.parameters.propertiesIndex = inputConfig.propertiesIndex;
  }

  private updateMapsConfig(inputConfig: ObjectSearchAppParametersInput) {
    const googleMapsApiKey = inputConfig.googleMapsApiKey;
    const mapGridSize = inputConfig.mapGridSize || 0;

    if (googleMapsApiKey) {
      this.parameters.googleMapsApiKey = googleMapsApiKey;
      this.parameters.mapGridSize = mapGridSize;
    } else {
      throw new Error('No Google Maps Api Key specified in app parameters.');
    }
  }

  private updateDetailPageUrl(inputConfig: ObjectSearchAppParametersInput) {
    const detailPageUrl = inputConfig.detailPageUrl;

    if (detailPageUrl) {
      this.parameters.detailPageUrl = detailPageUrl;
    } else {
      throw new Error('No Detail URL specified in app parameters.');
    }
  }

  private updateInitialFilters(inputConfig: ObjectSearchAppParametersInput) {
    const initialFilters = inputConfig.initialFilters;
    if (initialFilters) {
      this.parameters.initialFilters = {};

      if (initialFilters.contractType) {
        this.parameters.initialFilters.contractType =
          availableContractTypeOptions.find((o: ContractTypeOption) => {
            return o.value === initialFilters.contractType;
          });

        if (initialFilters.price?.min || initialFilters.price?.max) {
          const priceOptions: PriceOption[] = getPriceOptionsForContractType(
            inputConfig.countries,
            initialFilters.contractType
          );

          const min = this.getOptionByValue(
            priceOptions,
            initialFilters.price.min
          );
          const max = this.getOptionByValue(
            priceOptions,
            initialFilters.price.max
          );

          this.parameters.initialFilters.price = {
            min: min || priceOptions[0],
            max: max || priceOptions[priceOptions.length - 1],
          };
        }

        if (initialFilters.roomCount?.min || initialFilters.roomCount?.max) {
          const roomCountOptions: RoomCountOption[] =
            getRoomCountOptionsForContractType(
              inputConfig.countries,
              initialFilters.contractType
            );

          const min = this.getOptionByValue(
            roomCountOptions,
            initialFilters.roomCount.min
          );
          const max = this.getOptionByValue(
            roomCountOptions,
            initialFilters.roomCount.max
          );

          this.parameters.initialFilters.roomCount = {
            min: min || roomCountOptions[0],
            max: max || roomCountOptions[roomCountOptions.length - 1],
          };
        }

        if (
          initialFilters.livingSpace?.min ||
          initialFilters.livingSpace?.max
        ) {
          const livingSpaceOptions: LivingSpaceOption[] =
            getLivingSpaceOptionsForContractType(
              inputConfig.countries,
              initialFilters.contractType
            );

          const min = this.getOptionByValue(
            livingSpaceOptions,
            initialFilters.livingSpace.min
          );
          const max = this.getOptionByValue(
            livingSpaceOptions,
            initialFilters.livingSpace.max
          );

          this.parameters.initialFilters.livingSpace = {
            min: min || livingSpaceOptions[0],
            max: max || livingSpaceOptions[livingSpaceOptions.length - 1],
          };
        }

        if (
          initialFilters.propertySpace?.min ||
          initialFilters.propertySpace?.max
        ) {
          const propertySpaceOptions: PropertySpaceOption[] =
            getPropertySpaceOptionsForContractType(
              inputConfig.countries,
              initialFilters.contractType
            );

          const min = this.getOptionByValue(
            propertySpaceOptions,
            initialFilters.propertySpace.min
          );
          const max = this.getOptionByValue(
            propertySpaceOptions,
            initialFilters.propertySpace.max
          );

          this.parameters.initialFilters.propertySpace = {
            min: min || propertySpaceOptions[0],
            max: max || propertySpaceOptions[propertySpaceOptions.length - 1],
          };
        }

        if (initialFilters.usageSpace?.min || initialFilters.usageSpace?.max) {
          const usageSpaceOptions: UsageSpaceOption[] =
            getUsageSpaceForContractType(initialFilters.contractType);

          const min = this.getOptionByValue(
            usageSpaceOptions,
            initialFilters.usageSpace.min
          );
          const max = this.getOptionByValue(
            usageSpaceOptions,
            initialFilters.usageSpace.max
          );

          this.parameters.initialFilters.usageSpace = {
            min: min || usageSpaceOptions[0],
            max: max || usageSpaceOptions[usageSpaceOptions.length - 1],
          };
        }
      }

      if (initialFilters.category) {
        this.parameters.initialFilters.category = {};

        if (initialFilters.category.main) {
          this.parameters.initialFilters.category.main =
            categoryFilterOptions.allOptions.find((o: CategoryFilterOption) => {
              return o.value === initialFilters.category.main;
            });
        }

        if (initialFilters.category.sub?.length > 0) {
          const initialSubcategories = initialFilters.category.sub;
          const subcategoriesMap = cloneSubcategoryFilter(
            categoryToSubcategoriesMap
          );

          for (const key of Object.keys(subcategoriesMap)) {
            const subcategoryOptions = subcategoriesMap[key as Category];

            for (const option of subcategoryOptions) {
              if (initialSubcategories.indexOf(option.value) > -1) {
                option.isChecked = true;
              }
            }
          }

          this.parameters.initialFilters.category.sub = subcategoriesMap;
        }
      }
    }
  }

  private getOptionByValue<T>(options: FilterOption<T>[], value: T | null) {
    return options.find((o) => o.value === value);
  }
}
